/**
 * Data Mapping Utility
 * Transforms extracted data from backend to match frontend form field requirements
 */

// Gender mapping
const GENDER_MAP = {
  'M': 'Male',
  'F': 'Female',
  'Male': 'Male',
  'Female': 'Female',
  'MALE': 'Male',
  'FEMALE': 'Female',
  'm': 'Male',
  'f': 'Female',
};

// Passport type mapping
const PASSPORT_TYPE_MAP = {
  // Ordinary passport variations
  'PM': 'Ordinary Passport',
  'P': 'Ordinary Passport',
  'REGULAR': 'Ordinary Passport',
  'Regular': 'Ordinary Passport',
  'regular': 'Ordinary Passport',
  'ORDINARY': 'Ordinary Passport',
  'Ordinary': 'Ordinary Passport',
  'ordinary': 'Ordinary Passport',
  'Ordinary Passport': 'Ordinary Passport',
  
  // Official passport
  'OFFICIAL': 'Official Passport',
  'Official': 'Official Passport',
  'official': 'Official Passport',
  'Official Passport': 'Official Passport',
  
  // Diplomatic passport
  'DIPLOMATIC': 'Diplomatic Passport',
  'Diplomatic': 'Diplomatic Passport',
  'diplomatic': 'Diplomatic Passport',
  'Diplomatic Passport': 'Diplomatic Passport',
  'D': 'Diplomatic Passport',
  
  // Service passport
  'SERVICE': 'Service Passport',
  'Service': 'Service Passport',
  'service': 'Service Passport',
  'Service Passport': 'Service Passport',
  'S': 'Service Passport',
  
  // Special passport
  'SPECIAL': 'Special Passport',
  'Special': 'Special Passport',
  'special': 'Special Passport',
  'Special Passport': 'Special Passport',
};

// Month mapping for date conversion
const MONTH_MAP = {
  'JAN': '01', 'JANUARY': '01',
  'FEB': '02', 'FEBRUARY': '02',
  'MAR': '03', 'MARCH': '03',
  'APR': '04', 'APRIL': '04',
  'MAY': '05',
  'JUN': '06', 'JUNE': '06',
  'JUL': '07', 'JULY': '07',
  'AUG': '08', 'AUGUST': '08',
  'SEP': '09', 'SEPT': '09', 'SEPTEMBER': '09',
  'OCT': '10', 'OCTOBER': '10',
  'NOV': '11', 'NOVEMBER': '11',
  'DEC': '12', 'DECEMBER': '12',
};

/**
 * Convert various date formats to YYYY-MM-DD
 * Handles formats like:
 * - "27 SEP 1977"
 * - "27-SEP-1977"
 * - "27/09/1977"
 * - "1977-09-27" (already correct)
 */
export const normalizeDate = (dateString) => {
  if (!dateString) return '';

  // Remove extra whitespace
  const cleaned = dateString.trim();

  // Already in correct format (YYYY-MM-DD)
  if (/^\d{4}-\d{2}-\d{2}$/.test(cleaned)) {
    return cleaned;
  }

  // Handle "DD MMM YYYY" or "DD-MMM-YYYY" format
  const ddMmmYyyy = /^(\d{1,2})[\s-]([A-Za-z]+)[\s-](\d{4})$/;
  const match1 = cleaned.match(ddMmmYyyy);
  
  if (match1) {
    const [, day, month, year] = match1;
    const monthNum = MONTH_MAP[month.toUpperCase()];
    
    if (monthNum) {
      const paddedDay = day.padStart(2, '0');
      return `${year}-${monthNum}-${paddedDay}`;
    }
  }

  // Handle "DD/MM/YYYY" format
  const ddMmYyyy = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;
  const match2 = cleaned.match(ddMmYyyy);
  
  if (match2) {
    const [, day, month, year] = match2;
    const paddedDay = day.padStart(2, '0');
    const paddedMonth = month.padStart(2, '0');
    return `${year}-${paddedMonth}-${paddedDay}`;
  }

  // Handle "YYYY/MM/DD" format
  const yyyyMmDd = /^(\d{4})\/(\d{1,2})\/(\d{1,2})$/;
  const match3 = cleaned.match(yyyyMmDd);
  
  if (match3) {
    const [, year, month, day] = match3;
    const paddedDay = day.padStart(2, '0');
    const paddedMonth = month.padStart(2, '0');
    return `${year}-${paddedMonth}-${paddedDay}`;
  }

  // If we can't parse it, return empty string
  console.warn(`Unable to parse date: ${dateString}`);
  return '';
};

/**
 * Normalize gender value
 */
export const normalizeGender = (gender) => {
  if (!gender) return '';
  
  const normalized = GENDER_MAP[gender.trim()];
  
  if (!normalized) {
    console.warn(`Unknown gender value: ${gender}`);
    return gender; // Return original if not found
  }
  
  return normalized;
};

/**
 * Normalize passport type value
 */
export const normalizePassportType = (passportType) => {
  if (!passportType) return '';
  
  const normalized = PASSPORT_TYPE_MAP[passportType.trim()];
  
  if (normalized === undefined) {
    console.warn(`Unknown passport type value: ${passportType}`);
    return passportType; // Return original if not found
  }
  
  return normalized;
};

/**
 * Normalize YES/NO values
 */
export const normalizeYesNo = (value) => {
  if (!value) return '';
  
  const cleaned = value.trim().toUpperCase();
  
  if (cleaned === 'YES' || cleaned === 'Y' || cleaned === '1' || cleaned === 'TRUE') {
    return 'YES';
  }
  
  if (cleaned === 'NO' || cleaned === 'N' || cleaned === '0' || cleaned === 'FALSE') {
    return 'NO';
  }
  
  return value;
};

/**
 * Normalize phone numbers
 * Removes special characters but keeps numbers and +
 */
export const normalizePhone = (phone) => {
  if (!phone) return '';
  
  // Keep only digits, +, and spaces
  return phone.replace(/[^\d+\s]/g, '').trim();
};

/**
 * Detect marital status from spouse information
 */
const detectMaritalStatus = (extractedData, mappedData) => {
  // Priority 1: Use explicit marital status if provided
  const explicitStatus = extractedData['Marital Status'];
  if (explicitStatus && explicitStatus.trim() !== '') {
    const normalized = explicitStatus.toLowerCase().trim();
    if (normalized.includes('married')) return 'Married';
    if (normalized.includes('single') || normalized.includes('unmarried')) return 'Single';
    if (normalized.includes('divorce')) return 'Divorced';
    if (normalized.includes('widow')) return 'Widowed';
    return explicitStatus;
  }

  // Priority 2: Detect from spouse information
  const spouseFields = [
    extractedData['Spouse Name'],
    extractedData['Spouse Nationality'],
    extractedData['Spouse Birth Place'],
    extractedData['Spouse Birth Country'],
  ];

  const hasSpouseInfo = spouseFields.some(field => 
    field && String(field).trim() !== '' && String(field).toLowerCase() !== 'n/a'
  );

  if (hasSpouseInfo) {
    console.log('✅ Spouse information detected, setting marital status to Married');
    return 'Married';
  }

  // Priority 3: Default value
  return 'Single';
};

/**
 * Detect if name was changed based on previous name fields
 */
const detectNameChange = (extractedData) => {
  const previousSurname = extractedData['Previous Surname'];
  const previousGivenName = extractedData['Previous Given Name'];
  
  const hasPreviousName = 
    (previousSurname && previousSurname.trim() !== '') ||
    (previousGivenName && previousGivenName.trim() !== '');
  
  if (hasPreviousName) {
    console.log('✅ Previous name detected, setting name_change to YES');
    return 'YES';
  }
  
  return 'NO';
};

/**
 * Detect military service from organization/designation fields
 */
const detectMilitaryService = (extractedData) => {
  const militaryOrg = extractedData['Military Organization'];
  const militaryDesignation = extractedData['Military Designation'];
  const militaryRank = extractedData['Military Rank'];
  const militaryPosting = extractedData['Military Posting'];
  
  const hasMilitaryInfo = 
    (militaryOrg && militaryOrg.trim() !== '') ||
    (militaryDesignation && militaryDesignation.trim() !== '') ||
    (militaryRank && militaryRank.trim() !== '') ||
    (militaryPosting && militaryPosting.trim() !== '');
  
  if (hasMilitaryInfo) {
    console.log('✅ Military information detected, setting military_service to YES');
    return 'YES';
  }
  
  return 'NO';
};

/**
 * Detect other passport from fields
 */
const detectOtherPassport = (extractedData) => {
  const otherPassportNumber = extractedData['Other Passport Number'];
  const otherPassportCountry = extractedData['Other Passport Country'];
  const otherPassportPlace = extractedData['Other Passport Place of Issue'];
  const otherPassportNationality = extractedData['Other Passport Nationality'];
  
  const hasOtherPassport = 
    (otherPassportNumber && otherPassportNumber.trim() !== '') ||
    (otherPassportCountry && otherPassportCountry.trim() !== '') ||
    (otherPassportPlace && otherPassportPlace.trim() !== '') ||
    (otherPassportNationality && otherPassportNationality.trim() !== '');
  
  if (hasOtherPassport) {
    console.log('✅ Other passport information detected, setting has_other_passport to YES');
    return 'YES';
  }
  
  return 'NO';
};

/**
 * Detect Pakistani heritage
 */
const detectPakHeritage = (extractedData) => {
  const pakHeritageDetails = extractedData['Pak Heritage Details'];
  
  if (pakHeritageDetails && pakHeritageDetails.trim() !== '' && pakHeritageDetails.toLowerCase() !== 'n/a') {
    console.log('✅ Pak heritage details found, setting pak_heritage to YES');
    return 'YES';
  }
  
  return 'NO';
};

/**
 * Detect previous India visits
 */
const detectPreviousIndiaVisit = (extractedData) => {
  const prevStayAddress = extractedData['Previous Stay Address'];
  const citiesVisited = extractedData['Cities Visited'];
  
  const hasVisitInfo = 
    (prevStayAddress && prevStayAddress.trim() !== '' && prevStayAddress.toLowerCase() !== 'n/a') ||
    (citiesVisited && citiesVisited.trim() !== '' && citiesVisited.toLowerCase() !== 'n/a');
  
  if (hasVisitInfo) {
    console.log('✅ Previous India visit info detected, setting visited_india_before to YES');
    return 'YES';
  }
  
  return 'NO';
};

/**
 * Detect hotel booking
 */
const detectHotelBooking = (extractedData) => {
  const hotelName = extractedData['Hotel/Resort Name'];
  const hotelCity = extractedData['Hotel/Resort City'];
  
  const hasHotelInfo = 
    (hotelName && hotelName.trim() !== '' && hotelName.toLowerCase() !== 'n/a') ||
    (hotelCity && hotelCity.trim() !== '' && hotelCity.toLowerCase() !== 'n/a');
  
  if (hasHotelInfo) {
    console.log('✅ Hotel booking info detected, setting booked_hotel to YES');
    return 'YES';
  }
  
  return 'NO';
};

/**
 * Detect and parse SAARC visit entries
 * Supports both single and multiple entries
 */
const detectAndParseSaarcVisit = (extractedData) => {
  const saarcCountry = extractedData['SAARC Country'];
  const saarcYear = extractedData['SAARC Year'];
  const saarcVisits = extractedData['SAARC Visits'];
  
  // Check if we have any SAARC data
  const hasSaarcInfo = 
    (saarcCountry && saarcCountry.trim() !== '' && saarcCountry.toLowerCase() !== 'n/a') ||
    (saarcYear && saarcYear.trim() !== '' && saarcYear.toLowerCase() !== 'n/a') ||
    (saarcVisits && saarcVisits.trim() !== '' && saarcVisits.toLowerCase() !== 'n/a');
  
  if (!hasSaarcInfo) {
    return { saarc_visit: 'NO', saarc_entries: [] };
  }

  console.log('✅ SAARC visit info detected');

  // Parse multiple entries if separated by delimiter (e.g., semicolon, pipe, or newline)
  const parseMultipleEntries = (str) => {
    if (!str) return [];
    // Split by common delimiters
    return str.split(/[;|\n]/).map(s => s.trim()).filter(s => s !== '');
  };

  const countries = parseMultipleEntries(saarcCountry || '');
  const years = parseMultipleEntries(saarcYear || '');
  const visits = parseMultipleEntries(saarcVisits || '');

  // Determine the number of entries (max of all arrays)
  const maxEntries = Math.max(
    countries.length || 1,
    years.length || 1,
    visits.length || 1
  );

  // Build entries array
  const entries = [];
  for (let i = 0; i < maxEntries; i++) {
    const entry = {
      saarc_country: countries[i] || countries[0] || '',
      saarc_year: years[i] || years[0] || '',
      no_of_visits: visits[i] || visits[0] || '',
    };
    
    // Only add entry if at least one field has value
    if (entry.saarc_country || entry.saarc_year || entry.no_of_visits) {
      entries.push(entry);
    }
  }

  return {
    saarc_visit: 'YES',
    saarc_entries: entries.length > 0 ? entries : [{
      saarc_country: saarcCountry || '',
      saarc_year: saarcYear || '',
      no_of_visits: saarcVisits || '',
    }]
  };
};

/**
 * Detect visa refusal
 */
const detectVisaRefusal = (extractedData) => {
  const refusalDetails = extractedData['Refusal Details'];
  
  if (refusalDetails && refusalDetails.trim() !== '' && refusalDetails.toLowerCase() !== 'n/a') {
    console.log('✅ Visa refusal details found, setting visa_refused to YES');
    return 'YES';
  }
  
  return 'NO';
};

/**
 * Copy present address to permanent address if they're the same
 */
const handleSameAsPresent = (extractedData, mappedData) => {
  // Check if permanent address fields are empty or match present address
  const presentStreet = mappedData.present_address_street || '';
  const presentCity = mappedData.present_address_city || '';
  const presentState = mappedData.present_address_state || '';
  const presentZip = mappedData.present_address_zip || '';
  const presentCountry = mappedData.present_address_country || '';

  const permanentStreet = mappedData.permanent_street || '';
  const permanentCity = mappedData.permanent_city || '';
  const permanentState = mappedData.permanent_state || '';

  // If permanent address is empty, assume it's same as present
  const isPermanentEmpty = !permanentStreet && !permanentCity && !permanentState;

  // If permanent address matches present address exactly
  const isExactMatch = 
    permanentStreet === presentStreet &&
    permanentCity === presentCity &&
    permanentState === presentState;

  if (isPermanentEmpty || isExactMatch) {
    console.log('✅ Permanent address same as present, setting checkbox to true');
    mappedData.same_as_present_address = true;
    
    // Copy present address to permanent
    mappedData.permanent_street = presentStreet;
    mappedData.permanent_city = presentCity;
    mappedData.permanent_state = presentState;
    mappedData.permanent_address_zip = presentZip;
    mappedData.permanent_address_country = presentCountry;
  } else {
    mappedData.same_as_present_address = false;
  }

  return mappedData;
};

/**
 * Auto-detect all conditional field triggers
 */
const autoDetectConditionalFields = (extractedData, mappedData) => {
  // Marital Status
  if (!mappedData.marital_status) {
    mappedData.marital_status = detectMaritalStatus(extractedData, mappedData);
  }

  // Name Change
  if (!mappedData.name_change) {
    mappedData.name_change = detectNameChange(extractedData);
  }

  // Military Service
  if (!mappedData.military_service) {
    mappedData.military_service = detectMilitaryService(extractedData);
  }

  // Other Passport
  if (!mappedData.has_other_passport) {
    mappedData.has_other_passport = detectOtherPassport(extractedData);
  }

  // Pakistani Heritage
  if (!mappedData.pak_heritage) {
    mappedData.pak_heritage = detectPakHeritage(extractedData);
  }

  // Previous India Visit
  if (!mappedData.visited_india_before) {
    mappedData.visited_india_before = detectPreviousIndiaVisit(extractedData);
  }

  // Hotel Booking
  if (!mappedData.booked_hotel) {
    mappedData.booked_hotel = detectHotelBooking(extractedData);
  }

  // SAARC Visit - Handle dynamic entries
  if (!mappedData.saarc_visit) {
    const saarcData = detectAndParseSaarcVisit(extractedData);
    mappedData.saarc_visit = saarcData.saarc_visit;
    if (saarcData.saarc_entries.length > 0) {
      mappedData.saarc_entries = saarcData.saarc_entries;
    }
  }

  // Visa Refusal
  if (!mappedData.visa_refused) {
    mappedData.visa_refused = detectVisaRefusal(extractedData);
  }

  // Same as Present Address
  handleSameAsPresent(extractedData, mappedData);

  return mappedData;
};

/**
 * Main mapping function - applies appropriate normalization based on field type
 */
export const mapExtractedData = (extractedData, formConfig) => {
  if (!extractedData || !formConfig) return {};

  const mappedData = {};

  // Get all fields from all sections
  const allFields = [];
  
  // Handle both array and object section formats
  const sections = Array.isArray(formConfig.sections) 
    ? formConfig.sections 
    : Object.values(formConfig);

  sections.forEach(section => {
    // Skip sections with custom renderers that don't have fields
    if (section.useCustomRenderer && (!section.fields || section.fields.length === 0)) {
      return;
    }

    if (section.subTabs?.length) {
      section.subTabs.forEach(subTab => {
        allFields.push(...(subTab.fields || []));
      });
    }
    
    if (section.fields?.length) {
      allFields.push(...section.fields);
    }
  });

  // Map each extracted field to its normalized value
  Object.entries(extractedData).forEach(([key, value]) => {
    if (!value) return;

    // Skip SAARC fields as they're handled specially in autoDetectConditionalFields
    if (['SAARC Country', 'SAARC Year', 'SAARC Visits'].includes(key)) {
      return;
    }

    // Find the field definition
    const field = allFields.find(f => f.extract_key === key || f.name === key);
    
    if (!field) {
      console.warn(`No field definition found for: ${key}`);
      return;
    }

    let normalizedValue = value;

    // Apply normalization based on field type
    switch (field.type) {
      case 'date':
        normalizedValue = normalizeDate(value);
        break;
      
      case 'select':
        // Special handling for gender
        if (field.name === 'gender') {
          normalizedValue = normalizeGender(value);
        }
        // Special handling for passport type
        else if (field.name === 'passport_type') {
          normalizedValue = normalizePassportType(value);
        }
        // Other selects - just trim
        else {
          normalizedValue = typeof value === 'string' ? value.trim() : value;
        }
        break;
      
      case 'radio':
        // Most radio buttons are YES/NO
        if (field.options?.some(opt => opt.value === 'YES' || opt.value === 'NO')) {
          normalizedValue = normalizeYesNo(value);
        } else {
          normalizedValue = typeof value === 'string' ? value.trim() : value;
        }
        break;
      
      case 'phone':
        normalizedValue = normalizePhone(value);
        break;
      
      case 'text':
      case 'textarea':
        normalizedValue = typeof value === 'string' ? value.trim() : value;
        break;
      
      case 'dynamic_group':
        // Skip - handled in autoDetectConditionalFields
        return;
      
      default:
        normalizedValue = value;
    }

    // Only set if we have a valid normalized value
    if (normalizedValue !== '' && normalizedValue !== null && normalizedValue !== undefined) {
      mappedData[field.name] = normalizedValue;
    }
  });

  // ✅ AUTO-DETECT CONDITIONAL FIELDS (including SAARC entries and same address)
  autoDetectConditionalFields(extractedData, mappedData);

  return mappedData;
};

/**
 * Validate date is in correct format
 */
export const isValidDateFormat = (dateString) => {
  if (!dateString) return false;
  return /^\d{4}-\d{2}-\d{2}$/.test(dateString);
};

/**
 * Get field type for a given field name
 */
export const getFieldType = (fieldName, formConfig) => {
  const allFields = [];
  
  const sections = Array.isArray(formConfig.sections) 
    ? formConfig.sections 
    : Object.values(formConfig);
  
  sections.forEach(section => {
    if (section.subTabs?.length) {
      section.subTabs.forEach(subTab => {
        allFields.push(...(subTab.fields || []));
      });
    }
    
    if (section.fields?.length) {
      allFields.push(...section.fields);
    }
  });

  const field = allFields.find(f => f.name === fieldName);
  return field?.type || 'text';
};

export default {
  mapExtractedData,
  normalizeDate,
  normalizeGender,
  normalizePassportType,
  normalizeYesNo,
  normalizePhone,
  isValidDateFormat,
  getFieldType,
};